import React from 'react';
import { Box, Card, Typography, Button, Stack } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';

export type PlanName = 'PERSONAL' | 'BUSINESS' | 'BUSINESS_PLUS';
export type BillingCycle = 'MONTHLY' | 'YEARLY';

export interface PlanCard {
  name: PlanName;
  displayName: string;
  priceMonthly: number;
  priceYearly: number;
  leadsPerMonth: number;
  users: number;
  features: string[];
  highlighted?: boolean;
  tagline?: string;
  /**
   * The one upgrade reason that distinguishes this plan from the one below.
   * Rendered as a bold lead-in above the features list so users can scan
   * across cards and immediately see "what do I get for the next tier".
   */
  differentiator?: string;
}

const DEFAULT_PLANS: PlanCard[] = [
  {
    name: 'PERSONAL',
    displayName: 'Personal',
    tagline: 'For solo operators getting started.',
    differentiator: 'AI lead generation, on your own.',
    priceMonthly: 199,
    priceYearly: 199 * 12 * 0.75,
    leadsPerMonth: 15,
    users: 1,
    features: ['15 leads / month', '1 user', 'Lead generation'],
  },
  {
    name: 'BUSINESS',
    displayName: 'Business',
    tagline: 'For small teams that need a CRM.',
    differentiator: 'Everything in Personal, plus the full CRM.',
    priceMonthly: 299,
    priceYearly: 299 * 12 * 0.75,
    leadsPerMonth: 50,
    users: 3,
    features: ['50 leads / month', '3 users', 'CRM (Client + AI Assistant)'],
    highlighted: true,
  },
  {
    name: 'BUSINESS_PLUS',
    displayName: 'Business Plus',
    tagline: 'For organizations with a lead manager.',
    differentiator: 'Everything in Business, plus SuperAdmin assignment.',
    priceMonthly: 499,
    priceYearly: 499 * 12 * 0.75,
    leadsPerMonth: 90,
    users: 5,
    features: [
      '90 leads / month',
      '5 users',
      'SuperAdmin lead assignment',
    ],
  },
];

interface PricingCardsProps {
  selectedPlan?: PlanName;
  onSelectPlan?: (plan: PlanName) => void;
  cycle?: BillingCycle;
  onCycleChange?: (cycle: BillingCycle) => void;
  ctaLabel?: string;
  plans?: PlanCard[];
  /**
   * If set, the card matching this plan name is treated as the user's CURRENT
   * subscription. It gets a "Current plan" badge instead of a "Selected" CTA,
   * and the other cards' buttons stay as "Switch to this" so the user can
   * always upgrade — even when their current entitlements are promo-modified
   * and don't match any standard tier 1:1.
   */
  currentPlan?: PlanName;
  /**
   * Optional small label shown on the current-plan card (e.g. "Trial — 22 days
   * left", "Active", "Promo active"). Renders next to the plan name as a chip.
   */
  currentPlanBadge?: string;
}

// Pricing pages should display whole-dollar amounts — fractional cents from
// yearly/12 math are a visual nit, not an actual billing precision.
const formatMoney = (value: number, withSuffix?: string): string => {
  const rounded = Math.round(value);
  return `US$${rounded.toLocaleString('en-US')}${withSuffix ?? ''}`;
};

// Custom color tokens — a single deep "ink" we use only for the highlighted
// card, so it reads as the one definitive recommendation on the page.
const INK = '#0B1220';
const INK_SOFT = '#1A2238';
const ACCENT = '#3B5BFF';
const SURFACE = '#FFFFFF';
const SURFACE_MUTED = '#F7F8FB';
const TEXT_MUTED = '#5B6478';
const BORDER = '#E5E8EF';

/**
 * Sliding pill toggle. Indicator animates between positions for tactile feel,
 * and the active label is high-contrast against the slider rather than against
 * the bare track (MUI default's biggest weakness).
 */
const CycleToggle: React.FC<{
  cycle: BillingCycle;
  onChange?: (c: BillingCycle) => void;
}> = ({ cycle, onChange }) => {
  const isYearly = cycle === 'YEARLY';
  return (
    <Box
      role="tablist"
      aria-label="Billing cycle"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        p: '4px',
        borderRadius: 999,
        backgroundColor: SURFACE_MUTED,
        border: `1px solid ${BORDER}`,
      }}
    >
      {/* Sliding indicator */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 4,
          bottom: 4,
          left: isYearly ? 4 : '50%',
          width: 'calc(50% - 4px)',
          borderRadius: 999,
          backgroundColor: INK,
          transition: 'left 220ms cubic-bezier(0.32, 0.72, 0, 1)',
          boxShadow: '0 1px 2px rgba(11, 18, 32, 0.18)',
        }}
      />
      {(['YEARLY', 'MONTHLY'] as BillingCycle[]).map((value) => {
        const active = value === cycle;
        const isYearly = value === 'YEARLY';
        return (
          <Box
            key={value}
            component="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange?.(value)}
            sx={{
              position: 'relative',
              zIndex: 1,
              minWidth: isYearly ? 168 : 108,
              py: '8px',
              px: '12px',
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 600,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: active ? '#FFFFFF' : TEXT_MUTED,
              transition: 'color 220ms ease',
              display: 'inline-flex',
              alignItems: 'center',
              // Pin contents to the start so "YEARLY" sits at the left edge
              // and the -25% pill sits next to it, instead of the pair being
              // centered as a group inside the button's min-width.
              placeContent: 'flex-start',
              gap: 1,
              '&:focus-visible': {
                outline: `2px solid ${ACCENT}`,
                outlineOffset: 2,
                borderRadius: 999,
              },
            }}
          >
            <span>{isYearly ? 'Yearly' : 'Monthly'}</span>
            {isYearly && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  px: 0.875,
                  // Use an explicit height so the pill cannot grow taller than
                  // the YEARLY label. Vertical padding implicit via flex centering.
                  height: 18,
                  display: 'inline-flex',
                  alignItems: 'center',
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 700,
                  lineHeight: 1,
                  letterSpacing: '0.06em',
                  backgroundColor: active ? 'rgba(124, 255, 184, 0.18)' : 'rgba(34, 139, 87, 0.12)',
                  color: active ? '#7CFFB8' : '#1B5E3A',
                  border: active
                    ? '1px solid rgba(124, 255, 184, 0.32)'
                    : '1px solid rgba(34, 139, 87, 0.28)',
                  transition: 'all 220ms ease',
                }}
              >
                -25%
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export const PricingCards: React.FC<PricingCardsProps> = ({
  selectedPlan,
  onSelectPlan,
  cycle = 'YEARLY',
  onCycleChange,
  ctaLabel = 'Choose',
  plans = DEFAULT_PLANS,
  currentPlan,
  currentPlanBadge,
}) => {
  return (
    <Box
      sx={{
        // Scoped font preference — falls back gracefully. We use a serif display
        // accent for prices (tabular numerals) and a clean sans for everything else.
        '--pc-font-display': '"Fraunces", "Tiempos Headline", "Georgia", serif',
        '--pc-font-sans': '"Inter", "SF Pro Text", system-ui, sans-serif',
      }}
    >
      {/* Toggle row — "-25%" is now inline on the Yearly label, not a separate chip */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: { xs: 4, md: 5 } }}>
        <CycleToggle cycle={cycle} onChange={onCycleChange} />
      </Box>

      {/* Cards grid — note the extra top padding on the row so the highlighted
          card's ribbon (which sits *inside* its own bounds, see below) has no
          chance of being clipped by any ancestor with overflow: hidden. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
          gap: { xs: 2.5, md: 3 },
          alignItems: 'stretch',
        }}
      >
        {plans.map((plan) => {
          const isSelected = selectedPlan === plan.name;
          const isCurrent = currentPlan === plan.name;
          const isHighlighted = !!plan.highlighted;
          const monthlyDisplay = cycle === 'YEARLY' ? plan.priceYearly / 12 : plan.priceMonthly;

          // Dark vs light card style. The dark card is the one definitive
          // recommendation — it carries the brand "ink" color the rest of the
          // page doesn't use, so it draws the eye without needing scale tricks.
          const isDark = isHighlighted;
          const bg = isDark ? INK : SURFACE;
          const fg = isDark ? '#FFFFFF' : INK;
          const fgMuted = isDark ? 'rgba(255, 255, 255, 0.68)' : TEXT_MUTED;
          const checkColor = isDark ? '#7CFFB8' : '#1B8A4A';
          // Border priority: selected > current > highlighted-default > muted.
          const borderColor = isSelected
            ? ACCENT
            : isCurrent
            ? '#1B8A4A' // green for current plan
            : isDark
            ? INK_SOFT
            : BORDER;

          return (
            <Card
              key={plan.name}
              elevation={0}
              onClick={() => onSelectPlan?.(plan.name)}
              sx={{
                position: 'relative',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minWidth: 0, // critical: lets children shrink in narrow columns instead of overflowing
                borderRadius: 3,
                backgroundColor: bg,
                color: fg,
                border: `1px solid ${borderColor}`,
                boxShadow: isSelected
                  ? `0 0 0 3px rgba(59, 91, 255, 0.18), 0 8px 24px rgba(11, 18, 32, 0.08)`
                  : isDark
                  ? '0 12px 32px rgba(11, 18, 32, 0.18)'
                  : '0 1px 0 rgba(11, 18, 32, 0.02)',
                overflow: 'hidden', // safe — the ribbon now lives inside the card
                transition:
                  'border-color 180ms ease, box-shadow 220ms ease, transform 220ms ease',
                '&:hover': {
                  borderColor: isSelected ? ACCENT : isDark ? '#2A3553' : '#CFD4DF',
                  transform: 'translateY(-2px)',
                },
                fontFamily: 'var(--pc-font-sans)',
              }}
            >
              {/* In-card ribbon — sits in the normal flow, so it CANNOT be
                  clipped by any ancestor overflow. The card's own overflow
                  hidden gives the ribbon clean rounded top corners. */}
              {isHighlighted && (
                <Box
                  sx={{
                    backgroundColor: ACCENT,
                    color: '#FFFFFF',
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                    py: '6px',
                    fontFamily: 'var(--pc-font-sans)',
                  }}
                >
                  ★ Most Popular
                </Box>
              )}

              {/* Inner content */}
              <Box
                sx={{
                  p: { xs: 2.5, sm: 3, md: 3.5 },
                  pt: isHighlighted ? { xs: 2.5, sm: 3, md: 3.5 } : { xs: 3, sm: 3.5, md: 4 },
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {/* Plan name + tagline */}
                <Box mb={3}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: 'var(--pc-font-sans)',
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        color: fgMuted,
                      }}
                    >
                      {plan.displayName}
                    </Typography>
                    {isCurrent && (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          height: 18,
                          px: 0.875,
                          borderRadius: 999,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.06em',
                          textTransform: 'uppercase',
                          color: isDark ? '#7CFFB8' : '#1B5E3A',
                          backgroundColor: isDark
                            ? 'rgba(124, 255, 184, 0.16)'
                            : 'rgba(34, 139, 87, 0.12)',
                          border: isDark
                            ? '1px solid rgba(124, 255, 184, 0.32)'
                            : '1px solid rgba(34, 139, 87, 0.28)',
                        }}
                      >
                        {currentPlanBadge || 'Current'}
                      </Box>
                    )}
                  </Box>
                  {plan.tagline && (
                    <Typography
                      component="div"
                      sx={{
                        fontFamily: 'var(--pc-font-sans)',
                        fontSize: 14,
                        lineHeight: 1.5,
                        color: fgMuted,
                        minHeight: 42, // align across cards regardless of length
                      }}
                    >
                      {plan.tagline}
                    </Typography>
                  )}
                </Box>

                {/* Price — uses a display serif with tabular numerals, allowed
                    to scale down on narrow columns via clamp() so it never gets
                    visually clipped. */}
                <Box mb={1} sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 1,
                      flexWrap: 'wrap',
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: 'var(--pc-font-display)',
                        fontWeight: 600,
                        fontSize: 'clamp(1.75rem, 3.2vw, 2.75rem)',
                        lineHeight: 1,
                        letterSpacing: '-0.02em',
                        fontFeatureSettings: '"tnum" 1, "ss01" 1',
                        color: fg,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatMoney(monthlyDisplay)}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontFamily: 'var(--pc-font-sans)',
                        fontSize: 13,
                        fontWeight: 500,
                        color: fgMuted,
                      }}
                    >
                      / month
                    </Typography>
                  </Box>

                  <Typography
                    component="div"
                    sx={{
                      mt: 1,
                      fontFamily: 'var(--pc-font-sans)',
                      fontSize: 12,
                      color: fgMuted,
                      letterSpacing: '0.01em',
                      minHeight: 32, // reserve space so cards line up whether yearly or monthly
                    }}
                  >
                    {cycle === 'YEARLY' ? (
                      <>
                        Billed yearly &middot; {formatMoney(plan.priceYearly)} / yr
                      </>
                    ) : (
                      <>Billed monthly</>
                    )}
                  </Typography>
                </Box>

                {/* Divider — hairline, doubles as visual breath between price and value props */}
                <Box
                  sx={{
                    height: '1px',
                    width: '100%',
                    backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : BORDER,
                    my: 3,
                    flexShrink: 0,
                  }}
                />

                {/* Differentiator lead-in — bold, single line, calls out the upgrade reason */}
                {plan.differentiator && (
                  <Typography
                    component="div"
                    sx={{
                      fontFamily: 'var(--pc-font-sans)',
                      fontSize: 13,
                      fontWeight: 600,
                      lineHeight: 1.45,
                      color: fg,
                      mb: 2,
                      minHeight: 36, // align lead-in lines across cards
                    }}
                  >
                    {plan.differentiator}
                  </Typography>
                )}

                {/* Features */}
                <Stack
                  spacing={1.5}
                  flex={1}
                  sx={{ minWidth: 0 }}
                >
                  {plan.features.map((f) => (
                    <Box
                      key={f}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.25,
                        minWidth: 0,
                      }}
                    >
                      <CheckIcon
                        sx={{
                          fontSize: 16,
                          color: checkColor,
                          mt: '3px',
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: 'var(--pc-font-sans)',
                          fontSize: 14,
                          lineHeight: 1.55,
                          color: fg,
                          minWidth: 0,
                          wordBreak: 'break-word',
                        }}
                      >
                        {f}
                      </Typography>
                    </Box>
                  ))}
                </Stack>

                {/* CTA — three states:
                    1. Current plan → disabled "Current plan" button (passive label).
                    2. Selected for purchase → bright accent "Selected" CTA.
                    3. Other plans → "Switch to this" with the dark-or-light style. */}
                <Button
                  fullWidth
                  disableElevation
                  disabled={isCurrent && !isSelected}
                  size="large"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCurrent && !isSelected) return; // can't re-buy current plan
                    onSelectPlan?.(plan.name);
                  }}
                  sx={{
                    mt: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontFamily: 'var(--pc-font-sans)',
                    fontWeight: 600,
                    fontSize: 14,
                    letterSpacing: '0.02em',
                    textTransform: 'none',
                    transition: 'background-color 180ms ease, color 180ms ease',
                    ...(isCurrent && !isSelected
                      ? {
                          backgroundColor: 'transparent',
                          color: isDark ? 'rgba(255,255,255,0.55)' : '#9AA1AE',
                          border: isDark
                            ? '1px dashed rgba(255,255,255,0.25)'
                            : `1px dashed #D9DEE7`,
                          cursor: 'default',
                          '&.Mui-disabled': {
                            color: isDark ? 'rgba(255,255,255,0.55)' : '#9AA1AE',
                            border: isDark
                              ? '1px dashed rgba(255,255,255,0.25)'
                              : `1px dashed #D9DEE7`,
                          },
                        }
                      : isSelected
                      ? {
                          backgroundColor: ACCENT,
                          color: '#FFFFFF',
                          '&:hover': { backgroundColor: '#2F4BD8' },
                        }
                      : isDark
                      ? {
                          backgroundColor: '#FFFFFF',
                          color: INK,
                          '&:hover': { backgroundColor: '#F0F2F7' },
                        }
                      : {
                          backgroundColor: 'transparent',
                          color: INK,
                          border: `1px solid ${INK}`,
                          '&:hover': { backgroundColor: INK, color: '#FFFFFF' },
                        }),
                  }}
                >
                  {isCurrent && !isSelected
                    ? 'Current plan'
                    : isSelected
                    ? '✓ Selected'
                    : ctaLabel}
                </Button>
              </Box>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

export default PricingCards;
export { DEFAULT_PLANS };
